import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { DPSuggestionKinds } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind, KgRootKind } from '@/types/schema';
import { PulidKind } from '@/utils/pulid';

interface UseDPSuggestionKindsProps {
  firstDeathClientId: string | null;
  targetKind: PulidKind | null;
}

/**
 * Given the first death client id, and the type of thing that is disposing of
 * assets, return the AI DP suggestion-related enums.
 */
export function useDPSuggestionKinds({
  firstDeathClientId,
  targetKind,
}: UseDPSuggestionKindsProps): DPSuggestionKinds | undefined {
  const { primaryClients } = useHouseholdDetailsContext();

  return useMemo(() => {
    if (isEmpty(primaryClients)) return undefined;

    const pcIndex = primaryClients?.findIndex(
      (pc) => pc.id === firstDeathClientId
    );

    const isFirstPC = pcIndex === 0;

    if (targetKind === PulidKind.TestamentaryEntity) {
      return {
        aiSuggestionKind: isFirstPC
          ? (AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst as MatchableKind)
          : (AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst as MatchableKind),
        matcherVariantKind: isFirstPC
          ? AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST
          : AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST,
        kgRootKind: KgRootKind.TestamentaryEntityDispositiveProvisions,
      };
    }

    return {
      aiSuggestionKind: isFirstPC
        ? (AiSuggestionKind.DpRecipientGrantor_1DiesFirst as MatchableKind)
        : (AiSuggestionKind.DpRecipientGrantor_2DiesFirst as MatchableKind),
      matcherVariantKind: isFirstPC
        ? AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST
        : AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST,
      kgRootKind: isFirstPC
        ? KgRootKind.EntityDispositiveProvisionsGrantor_1DiesFirst
        : KgRootKind.EntityDispositiveProvisionsGrantor_2DiesFirst,
    };
  }, [firstDeathClientId, primaryClients, targetKind]);
}
